import { Link } from "wouter";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Link href="/">
            <Logo size="md" />
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose dark:prose-invert max-w-none">
          <h1>Terms of Service</h1>
          <p className="text-muted-foreground italic">
            Last Updated: February 22, 2026
          </p>

          <section className="mt-8">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing, downloading, or using DailyHisaab ("App"), operated
              by ASL Digital Solutions ("Company," "we," "our," or "us"), you
              agree to be bound by these Terms of Service ("Terms"). If you do
              not agree to these Terms, you must not use the App.
            </p>
            <p>
              These Terms constitute a legally binding agreement between you
              ("User," "you," or "your") and the Company.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              DailyHisaab is a mobile-first billing and inventory management
              application designed for Indian small and medium businesses
              (SMBs). The App provides:
            </p>
            <ul>
              <li>Product and inventory management</li>
              <li>Sales and expense transaction recording</li>
              <li>Dashboard analytics and reporting</li>
              <li>Invoice generation and sharing</li>
              <li>Barcode scanning for product identification</li>
              <li>Google Sheets synchronization for data backup</li>
              <li>Reward points system and Pro mode features</li>
            </ul>
          </section>

          <section>
            <h2>3. Account Registration and Security</h2>
            <h3>3.1 Google Account</h3>
            <p>
              You must sign in with a valid Google account to use DailyHisaab.
              By signing in, you authorize us to access certain Google services
              (Sheets and Drive) on your behalf as described in our{" "}
              <Link href="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              .
            </p>

            <h3>3.2 Account Responsibility</h3>
            <p>You are responsible for:</p>
            <ul>
              <li>
                Maintaining the security of your Google account credentials
              </li>
              <li>All activities that occur under your account</li>
              <li>Ensuring the accuracy of your business information</li>
              <li>
                Notifying us immediately of any unauthorized use of your account
              </li>
            </ul>
          </section>

          <section>
            <h2>4. User Modes and Reward Points</h2>
            <h3>4.1 Normal Mode</h3>
            <p>
              All users start in Normal mode with access to basic features
              including product management, transaction recording, and Google
              Sheets sync.
            </p>

            <h3>4.2 Pro Mode</h3>
            <p>
              Pro mode provides additional features and is accessible through
              reward points. Pro mode access is time-limited based on the plan
              selected.
            </p>

            <h3>4.3 Reward Points</h3>
            <p>
              Reward points are earned through app usage activities such as
              signing up, adding products, and recording transactions. Points
              are non-transferable, have no monetary value, and can only be
              redeemed within the App for Pro mode access. We reserve the right
              to modify the reward points system at any time.
            </p>
          </section>

          <section>
            <h2>5. Acceptable Use</h2>
            <p>
              You agree to use DailyHisaab only for lawful business purposes.
              You must not:
            </p>
            <ul>
              <li>Use the App for any illegal or unauthorized purpose</li>
              <li>
                Attempt to gain unauthorized access to the App's systems or
                other users' accounts
              </li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>
                Reverse engineer, decompile, or disassemble any part of the App
              </li>
              <li>Use automated systems (bots, scrapers) to access the App</li>
              <li>Manipulate or exploit the reward points system</li>
              <li>Upload malicious content, viruses, or harmful code</li>
              <li>Use the App to generate fraudulent invoices or records</li>
            </ul>
          </section>

          <section>
            <h2>6. Data Ownership</h2>
            <h3>6.1 Your Data</h3>
            <p>
              You retain full ownership of all business data you enter into
              DailyHisaab, including products, transactions, and business
              profile information. Your transaction and product data is stored
              in your personal Google Sheets within your Google Drive.
            </p>

            <h3>6.2 License to Use</h3>
            <p>
              By using the App, you grant us a limited, non-exclusive license to
              process your data solely for the purpose of providing and
              improving the App's services.
            </p>

            <h3>6.3 Data Accuracy</h3>
            <p>
              You are solely responsible for the accuracy and completeness of
              data you enter into the App. DailyHisaab is a recording tool and
              does not verify the accuracy of your business data, tax
              calculations, or financial records.
            </p>
          </section>

          <section>
            <h2>7. Intellectual Property</h2>
            <p>
              The App, including its design, code, logos, trademarks, and
              content (excluding your data), is the intellectual property of ASL
              Digital Solutions and is protected by applicable copyright and
              trademark laws.
            </p>
            <p>
              You may not copy, modify, distribute, sell, or lease any part of
              the App or its content without our prior written consent.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>
              The App integrates with third-party services including Google
              (Sheets, Drive, Firebase), Open Food Facts, and Open Products
              Facts. Your use of these services is subject to their respective
              terms of service and privacy policies. We are not responsible for
              the availability, accuracy, or practices of these third-party
              services.
            </p>
          </section>

          <section>
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              DailyHisaab is provided on an "AS IS" and "AS AVAILABLE" basis
              without warranties of any kind, whether express or implied,
              including but not limited to:
            </p>
            <ul>
              <li>Merchantability or fitness for a particular purpose</li>
              <li>Uninterrupted or error-free operation</li>
              <li>
                Accuracy or reliability of any information obtained through the
                App
              </li>
              <li>Compatibility with all devices or operating systems</li>
            </ul>
            <p>
              The App is a business management tool and does{" "}
              <strong>not</strong> provide accounting, tax, or legal advice. You
              should consult qualified professionals for such matters.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, ASL Digital
              Solutions and its officers, directors, employees, and agents shall
              not be liable for:
            </p>
            <ul>
              <li>
                Any indirect, incidental, special, consequential, or punitive
                damages
              </li>
              <li>Loss of profits, revenue, data, or business opportunities</li>
              <li>
                Damages arising from data loss, synchronization failures, or
                service interruptions
              </li>
              <li>
                Errors in tax calculations, invoice amounts, or financial
                reports generated by the App
              </li>
              <li>
                Any damages exceeding the amount you have paid to us (if any) in
                the 12 months preceding the claim
              </li>
            </ul>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless ASL Digital Solutions
              from any claims, damages, losses, or expenses (including legal
              fees) arising from your use of the App, violation of these Terms,
              or infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2>12. Termination</h2>
            <ul>
              <li>
                <strong>By You:</strong> You may stop using the App at any time.
                You can request deletion of your account and data as described
                in our{" "}
                <Link href="/data-deletion" className="text-primary underline">
                  Data Deletion Policy
                </Link>
                .
              </li>
              <li>
                <strong>By Us:</strong> We reserve the right to suspend or
                terminate your access to the App at any time, with or without
                notice, for any reason including violation of these Terms.
              </li>
            </ul>
            <p>
              Upon termination, your right to use the App ceases immediately.
              Your data in Google Sheets remains in your Google Drive and is not
              affected.
            </p>
          </section>

          <section>
            <h2>13. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Updated
              Terms will be posted on this page with a revised "Last Updated"
              date. Your continued use of the App after changes constitutes
              acceptance of the modified Terms.
            </p>
          </section>

          <section>
            <h2>14. Governing Law and Disputes</h2>
            <p>
              These Terms are governed by and construed in accordance with the
              laws of India. Any disputes arising from these Terms or your use
              of the App shall be subject to the exclusive jurisdiction of the
              courts in Kolkata, West Bengal, India.
            </p>
          </section>

          <section>
            <h2>15. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or
              invalid, the remaining provisions shall continue in full force and
              effect.
            </p>
          </section>

          <section>
            <h2>16. Contact Us</h2>
            <p>
              For questions or concerns regarding these Terms of Service, please
              contact us:
            </p>
            <ul>
              <li>
                <strong>Email:</strong> support@dailyhisaab.com
              </li>
              <li>
                <strong>Legal:</strong> contact@asldigitalsolutions.in
              </li>
              <li>
                <strong>Website:</strong>{" "}
                <a
                  href="https://dailyhisaab.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  dailyhisaab.com
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mb-safe">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Link href="/">
              <Logo size="sm" />
            </Link>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                ©2026 DailyHisaab.🇮🇳 All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
